import React, { useState, useEffect } from 'react';
import { Box, TextField, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import api, { enhancedApi } from './api';

function ClientSidebar({ onClose }) {
  const [clients, setClients] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [noRecentClients, setNoRecentClients] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await enhancedApi.getClients();
        const data = response.data;
        const flattenedData = data.map((item) => ({
          ...item,
          first_name: item.primary_contact?.first_name || '',
          last_name: item.primary_contact?.last_name || '',
        }));
        setClients(flattenedData);
      } catch (error) {
        setError('Failed to load clients.');
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchRecentClients = async () => {
      try {
        setLoading(true);
        const response = await enhancedApi.getRecentClients();
        if (response.data.message) {
          setRecentClients([]);
          setNoRecentClients(true);
        } else {
          const flattenedRecentClients = response.data.map((recentClient) => ({
            ...recentClient.client,
            first_name: recentClient.client.primary_contact?.first_name || '',
            last_name: recentClient.client.primary_contact?.last_name || '',
          }));
          setRecentClients(flattenedRecentClients);
          setNoRecentClients(false);
        }
      } catch (error) {
        setError('Failed to load recent clients.');
        setRecentClients([]);
        setNoRecentClients(true);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentClients();
  }, []);

  const handleClose = (event) => {
    event.stopPropagation();
    onClose();
    setSearchTerm('');
  };

  const filteredClients = clients.filter((client) =>
    `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClientClick = async (client) => {
    if (!client?.id) return;
    try {
      await enhancedApi.addRecentClient(client.id);
      setSearchTerm('');
      navigate(`/client/${client.id}`, { state: { client } });
      onClose(); // Close the sidebar after navigation
    } catch (error) {
      navigate(`/client/${client.id}`, { state: { client } });
      onClose(); // Close the sidebar after navigation
    }
  };

  return (
    <div className="client-menu-modern">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={700}>Clients</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search clients…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        sx={{
          borderRadius: 2,
          mb: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2 }
        }}
        InputLabelProps={{ shrink: false }}
      />

      {/* Loading and Error States */}
      {loading && <Typography sx={{ mb: 2 }}>Loading...</Typography>}
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {/* Navigation Links - Always visible at top */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 15,
            mb: 1,
            color: 'text.primary',
            '&:hover': { background: 'action.hover', color: 'primary.main' }
          }}
          onClick={() => { navigate('/client_list'); onClose(); }}
        >
          Client List <ChevronRightIcon sx={{ ml: 'auto', color: 'inherit' }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 15,
            mb: 1,
            color: 'text.primary',
            '&:hover': { background: 'action.hover', color: 'primary.main' }
          }}
          onClick={() => { navigate('/manage-client-portals'); onClose(); }}
        >
          Manage Client Portals <ChevronRightIcon sx={{ ml: 'auto', color: 'inherit' }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 15,
            mb: 1,
            color: 'text.primary',
            '&:hover': { background: 'action.hover', color: 'primary.main' }
          }}
          onClick={() => { navigate('/dashboard'); onClose(); }}
        >
          Dashboard <ChevronRightIcon sx={{ ml: 'auto', color: 'inherit' }} />
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: '1px solid #eee', my: 2, flexShrink: 0 }} />

      {/* Content Area with Scroll - Recent clients / Search results */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* Recent Clients */}
        {!loading && !error && !searchTerm && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <HistoryIcon fontSize="small" sx={{ mr: 1, color: '#888' }} />
              <Typography variant="subtitle2" color="text.secondary">Recent clients</Typography>
            </Box>
            {recentClients.length > 0 ? (
              recentClients.map(client => (
                <Typography
                  key={client.id}
                  variant="body2"
                  sx={{
                    color: '#222',
                    cursor: 'pointer',
                    mb: 0.5,
                    pl: 3,
                    py: 0.5,
                    borderRadius: 1,
                    '&:hover': { textDecoration: 'underline', color: 'primary.main', background: 'action.hover' }
                  }}
                  onClick={() => handleClientClick(client)}
                >
                  {client.first_name} {client.last_name}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>No recent clients</Typography>
            )}
          </Box>
        )}

        {/* Search Results */}
        {!loading && !error && searchTerm && (
          <Box mb={2}>
            {filteredClients.length > 0 ? (
              filteredClients.map(client => (
                <Typography
                  key={client.id}
                  variant="body2"
                  sx={{
                    color: '#222',
                    cursor: 'pointer',
                    mb: 0.5,
                    pl: 3,
                    py: 0.5,
                    borderRadius: 1,
                    '&:hover': { textDecoration: 'underline', color: 'primary.main', background: 'action.hover' }
                  }}
                  onClick={() => handleClientClick(client)}
                >
                  {client.first_name} {client.last_name}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>No clients found</Typography>
            )}
          </Box>
        )}
      </Box>
    </div>
  );
}

export default ClientSidebar;